import React, { useState } from 'react';
import { useResumesContext } from "../context/resumes-context";
import { startResumeGeneration } from '../api/resume';
import { motion } from 'framer-motion';

export const CraftView = () => {
  const { resumes, loading } = useResumesContext();
  const [selectedResumeId, setSelectedResumeId] = useState('');
  const [jdText, setJdText] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleGenerate = async () => {
    setError('');
    setSuccess('');
    
    if (!selectedResumeId) {
      setError('Please select a resume.');
      return;
    }
    
    if (!jdText.trim()) {
      setError('Please paste a job description.');
      return;
    }

    setIsGenerating(true);
    
    try {
      const { success, resp } = await startResumeGeneration(selectedResumeId, jdText, companyName);
      if (success) {
        setSuccess('Resume generation has started! You will be notified when it is complete.');
        setJdText('');
        setSelectedResumeId('');
        setCompanyName('');
      } else {
        setError(resp || 'Failed to start resume generation.');
      }
    } catch (err) {
      setError('An error occurred while starting the generation.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <section className="bg-gradient-to-b from-white to-indigo-50 px-8 py-20 w-full h-full flex-1 flex flex-col">
      <div className="container fade-in max-w-4xl mx-auto flex-1 flex flex-col">
        <div className="mb-10 block">
            <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl mb-4">Generate a Tailored Resume</h2>
            <p className="text-xl text-gray-500">Paste a job description and our AI will craft a resume specifically for that role.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="space-y-8">
                
                {/* Resume Dropdown */}
                <div>
                    <label htmlFor="resume-select" className="block text-sm font-medium text-gray-700 mb-2">
                        Select Base Resume <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <select
                            id="resume-select"
                            value={selectedResumeId}
                            onChange={(e) => setSelectedResumeId(e.target.value)}
                            disabled={loading}
                            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all text-gray-800 appearance-none"
                        >
                            <option value="" disabled>-- Select a resume --</option>
                            {resumes.map((resume) => (
                                <option key={resume.id} value={resume.id}>
                                    {resume.filename} {resume.role ? `- ${resume.role}` : ''}
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-700">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                        </div>
                    </div>
                    {resumes.length === 0 && !loading && (
                        <p className="text-sm text-amber-600 mt-2">You don't have any resumes uploaded yet. Please upload one first.</p>
                    )}
                </div>

                {/* Company Name */}
                <div>
                    <label htmlFor="company-name" className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <input
                        type="text"
                        id="company-name"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="e.g. Google, Stripe, Netflix"
                        className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all text-gray-800"
                    />
                </div>

                {/* JD Text area */}
                <div>
                    <label htmlFor="jd-text" className="block text-sm font-medium text-gray-700 mb-2">
                        Job Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        id="jd-text"
                        value={jdText}
                        onChange={(e) => setJdText(e.target.value)}
                        placeholder="Paste the target job description here..."
                        rows={8}
                        className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all text-gray-800 resize-y"
                    ></textarea>
                </div>

                {/* Errors & Success */}
                {error && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-lg border border-red-200 text-sm">
                        {error}
                    </div>
                )}
                
                {success && (
                    <div className="bg-green-50 text-green-600 p-4 rounded-lg border border-green-200 text-sm">
                        {success}
                    </div>
                )}

                {/* CTA */}
                <div className="pt-4 flex justify-end">
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        disabled={isGenerating || resumes.length === 0}
                        onClick={handleGenerate}
                        className={`
                            px-8 py-3 rounded-lg font-semibold text-white shadow-md transition-all
                            ${isGenerating || resumes.length === 0
                                ? 'bg-indigo-400 cursor-not-allowed'
                                : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-lg'
                            }
                        `}
                    >
                        {isGenerating ? (
                            <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Crafting Resume...
                            </span>
                        ) : 'Generate Resume'}
                    </motion.button>
                </div>
            </div>
        </div>
      </div>
    </section>
  );
};
